import {ApiClient} from '@hconnect/apiclient'
import {
  FilterTimeRange,
  EventSearchCriteria,
  ShiftEventsSortBy,
  SortOrder,
  Shift
} from '@hconnect/common/types'
import {generateTimeRange} from '@hconnect/common/utils'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment-timezone'

import {EventType, FilterOptions} from '../../types'
import {isStoppageEventType} from '../utils/eventType'
import {
  stoppageCodeSearchParamsToFilterParams,
  maintenanceNotificationSearchParamsToFilterParams
} from '../utils/stoppageHelper'

type UseEventsSearchDownloadParams = {
  plantId: string
  shifts: Shift[]
  getPlantNow: () => Moment
  input: FilterOptions
  sortBy?: ShiftEventsSortBy
  sortOrder?: SortOrder
  pageNumber?: number
  itemsPerPage?: number
}

type UseEventsSearchDownloadOptions = Omit<UseQueryOptions<Blob, AxiosError>, 'queryKey'>

export const QueryKey = 'events-search-download'

export const useEventsSearchDownload = (
  {
    plantId,
    shifts,
    getPlantNow,
    input,
    sortBy = ShiftEventsSortBy.Details,
    sortOrder,
    pageNumber = 0,
    itemsPerPage = 50
  }: UseEventsSearchDownloadParams,
  options?: UseEventsSearchDownloadOptions
) => {
  const timeRange: FilterTimeRange = generateTimeRange(input.timeRange, shifts, getPlantNow)

  return useQuery<Blob, AxiosError>({
    queryKey: [QueryKey, input, timeRange, pageNumber, itemsPerPage, sortBy, sortOrder, plantId],
    queryFn: async ({signal}) => {
      const isStoppage = input.eventType?.some((type: EventType) => isStoppageEventType(type))

      const settings: EventSearchCriteria = {
        timeRange,
        processStage: input.processStage,
        equipment: input.equipment,
        mainEquipment: input.mainEquipment,
        eventType: input.eventType,
        priority: input.priority,
        status: input.status,
        category: input.category,
        pageNumber,
        itemsPerPage,
        sortBy,
        sortOrder,
        stoppageCodesFilter: stoppageCodeSearchParamsToFilterParams(input.stoppageCode),
        maintenanceNotificationExists: maintenanceNotificationSearchParamsToFilterParams(
          input.maintenanceNotificationExists
        )
      }

      const response = await ApiClient.axiosInstance.post<Blob>(
        `/shifthandover/${plantId}/events/excel-export?includeStoppagesFromPxTrend=${!!isStoppage}`,
        settings,
        {responseType: 'blob', signal}
      )

      return response.data
    },
    enabled: false,
    refetchOnWindowFocus: false,
    ...options
  })
}
